import { GoodThing, DashboardMetrics, WeeklyStats } from '../types/good-things';
import { metricsCache } from './metricsCache';
import { employeeNamesService } from './employeeNames';
import { refreshManager } from './refreshManager';
import axios from 'axios';
import { getFunctionUrl } from '../config/api';
import { firestoreWrapper } from './firestoreWrapper';
import { auth } from '../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Create axios instance with configuration
const api = axios.create({
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  try {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  } catch (error) {
    
    return config;
  }
});

const getWeekBounds = (date: Date): { start: Date; end: Date } => {
  const start = new Date(date);
  start.setDate(date.getDate() - ((date.getDay() + 6) % 7)); // Start of week (Monday)
  start.setHours(0, 0, 0, 0);

  const end = new Date(start);
  end.setDate(start.getDate() + 6); // End of week (Sunday)
  end.setHours(23, 59, 59, 999);

  return { start, end };
};

// Cache configuration
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const DASHBOARD_METRICS_CACHE_KEY = 'dashboardMetricsCache';

interface CacheEntry {
  data: DashboardMetrics;
  timestamp: number;
}

// Load cache from localStorage
const loadCache = (userEmail?: string): CacheEntry | null => {
  const cacheKey = `${DASHBOARD_METRICS_CACHE_KEY}_${userEmail || 'global'}`;
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    try {
      return JSON.parse(cached);
    } catch (e) {
      
      return null;
    }
  }
  return null;
};

// Save cache to localStorage
const saveCache = (data: DashboardMetrics, userEmail?: string) => {
  try {
    const cacheKey = `${DASHBOARD_METRICS_CACHE_KEY}_${userEmail || 'global'}`;
    const cacheData: CacheEntry = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (e) {
    
  }
};

// Clear cache
export const clearCache = () => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      // Clear all Good Things related caches
      if (key.startsWith(DASHBOARD_METRICS_CACHE_KEY)) {
        localStorage.removeItem(key);
      }
    });
  } catch (e) {
    
  }
};

// Clear all caches and force fresh data
export const clearAllCaches = () => {
  try {
    // Clear localStorage caches
    clearCache();
    // Clear metrics cache
    metricsCache.clearAllCaches();
    // Clear in-memory caches
    // Clear employee names cache
    employeeNamesService.clearCache();
    goodThingsService.sortedEntriesCache.clear();
    // Clear random good thing cache
    import('./randomGoodThingService').then(module => {
      module.randomGoodThingService.clearCache();
    }).catch(error => {
      
    });
  } catch (e) {
    
  }
};

interface GoodThingsService {
  sortedEntriesCache: Map<string, GoodThing[]>;
  getAllEntries(): Promise<GoodThing[]>;
  getUserRecentGoodThings(userEmail: string, limit?: number, offset?: number): Promise<GoodThing[]>;
  getUserTotalGoodThingsCount(userEmail: string): Promise<number>;
  getWeeklyStats(weekDate: Date, page?: number, limit?: number): Promise<WeeklyStats>;
  getDashboardMetrics(userEmail?: string): Promise<DashboardMetrics>;
  addGoodThing(goodThing: Omit<GoodThing, 'id' | 'timestamp'>): Promise<void>;
  getDropdownData(): Promise<{ categories: string[]; people: string[]; }>;
  getSyncStatus(): Promise<{ lastSync: Date; entriesCount: number; inProgress: boolean; lastError?: string; }>;
  forceSync(): Promise<{ entriesCount: number; deletedCount: number }>;
  deduplicateEntries(entries: GoodThing[]): GoodThing[];
  createDeduplicationKey(entry: GoodThing): string;
  normalizeDateForDeduplication(date: string): string;
  selectBestEntry(entries: GoodThing[]): GoodThing;
}

// Create and export the goodThingsService
export const goodThingsService: GoodThingsService & {
  triggerFullSync(): Promise<{ success: boolean; result: any }>;
  updateGoodThing(goodThing: GoodThing, originalNote: string): Promise<void>;
  deleteGoodThing(goodThing: GoodThing): Promise<void>;
} = {
  sortedEntriesCache: new Map(),

  async getAllEntries(): Promise<GoodThing[]> {
    // Use the metrics cache which handles both Firestore and API data properly
    try {
      const result = await metricsCache.getGoodThingsEntries();
      
      // Apply deduplication logic to prioritize rich text versions
      const deduplicatedEntries = this.deduplicateEntries(result.items);
      
      // Sort by date descending
      deduplicatedEntries.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateB.getTime() - dateA.getTime();
      });
      
      return deduplicatedEntries;
    } catch (error) {
      
      return [];
    }
  },

  // Helper method to deduplicate entries, prioritizing rich text versions
  deduplicateEntries(entries: GoodThing[]): GoodThing[] {
    // Group entries by a composite key of date, submitter, category, and note content
    const entryGroups = new Map<string, GoodThing[]>();
    
    entries.forEach(entry => {
      // Create a normalized key for grouping similar entries
      const key = this.createDeduplicationKey(entry);
      
      if (!entryGroups.has(key)) {
        entryGroups.set(key, []);
      }
      entryGroups.get(key)!.push(entry);
    });
    
    // For each group, select the best entry (prioritize rich text)
    const deduplicatedEntries: GoodThing[] = [];
    
    entryGroups.forEach((groupEntries) => {
      if (groupEntries.length === 1) {
        // No duplicates, keep the single entry
        deduplicatedEntries.push(groupEntries[0]);
      } else {
        // Multiple entries found, prioritize rich text version
        const bestEntry = this.selectBestEntry(groupEntries);
        deduplicatedEntries.push(bestEntry);
      }
    });
    
    return deduplicatedEntries;
  },

  // Create a normalized key for grouping similar entries
  createDeduplicationKey(entry: GoodThing): string {
    // Normalize the note content by removing HTML tags and extra whitespace
    const normalizedNote = entry.note
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim()
      .toLowerCase();
    
    // Normalize date to handle different formats (ISO vs MM/DD/YY)
    const normalizedDate = this.normalizeDateForDeduplication(entry.date);
    
    // Create composite key
    return `${normalizedDate}|${entry.submitter.toLowerCase()}|${entry.category.toLowerCase()}|${normalizedNote}`;
  },

  // Normalize date formats for consistent comparison
  normalizeDateForDeduplication(date: string): string {
    try {
      // Try to parse as Date first (handles ISO format)
      const parsedDate = new Date(date);
      if (!isNaN(parsedDate.getTime())) {
        // Return as YYYY-MM-DD format
        return parsedDate.toISOString().split('T')[0];
      }
      
      // Handle MM/DD/YY format
      const parts = date.split('/');
      if (parts.length === 3) {
        const [month, day, year] = parts;
        const fullYear = year.length === 2 ? `20${year}` : year;
        return `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
      
      // Fallback to original date if parsing fails
      return date.toLowerCase();
    } catch (error) {
      
      return date.toLowerCase();
    }
  },

  // Select the best entry from a group of duplicates
  selectBestEntry(entries: GoodThing[]): GoodThing {
    // Priority order:
    // 1. Entries with rich text content
    // 2. Entries with more recent timestamps
    // 3. Entries with longer note content (more detailed)
    
    return entries.reduce((best, current) => {
      // Prioritize entries with rich text
      const bestHasRichText = best.richNote && Object.keys(best.richNote).length > 0;
      const currentHasRichText = current.richNote && Object.keys(current.richNote).length > 0;
      
      if (currentHasRichText && !bestHasRichText) {
        return current;
      }
      if (bestHasRichText && !currentHasRichText) {
        return best;
      }
      
      // If both have rich text or both don't, compare timestamps
      // Handle both number and Date timestamp formats
      const bestTimestamp = typeof best.timestamp === 'number'
        ? best.timestamp
        : (best.timestamp as any)?.getTime?.() || 0;
      const currentTimestamp = typeof current.timestamp === 'number'
        ? current.timestamp
        : (current.timestamp as any)?.getTime?.() || 0;
      
      if (currentTimestamp > bestTimestamp) {
        return current;
      }
      if (bestTimestamp > currentTimestamp) {
        return best;
      }
      
      // If timestamps are equal, prefer longer note content
      if (current.note.length > best.note.length) {
        return current;
      }
      
      return best;
    });
  },

  async updateGoodThing(goodThing: GoodThing, originalNote: string): Promise<void> {
    try {
      // Check if created today or yesterday (using EST timezone)
      const creationDate = new Date(goodThing.date);
      const now = new Date();
      
      // Convert dates to EST timezone for comparison
      const estOptions = { timeZone: 'America/New_York' };
      
      // Format today's date as MM/DD/YYYY in EST
      const todayFormatted = now.toLocaleDateString('en-US', estOptions);
      
      // Get yesterday's date in EST timezone
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayFormatted = yesterday.toLocaleDateString('en-US', estOptions);
      
      // Format creation date as MM/DD/YYYY in EST
      const creationDateFormatted = creationDate.toLocaleDateString('en-US', estOptions);
      
      // Allow editing if the Good Thing was created today OR yesterday
      const isWithinWindow = (todayFormatted === creationDateFormatted) || 
                            (yesterdayFormatted === creationDateFormatted);
      
      if (!isWithinWindow) {
        throw new Error('Good Things can only be edited on the day they were created or the day after');
      }
      
      // Include the original note to help identify the row
      const updateData = {
        ...goodThing,
        originalNote
      };
      
      await api.put(getFunctionUrl('goodThings'), updateData);
      
      // Clear caches to ensure fresh data
      clearAllCaches();
      
      // Trigger a full sync to update Firebase
      try {
        await this.triggerFullSync();
      } catch (syncError) {
        
        // Continue even if sync fails - we'll try again later
      }
    } catch (error) {
      
      throw error;
    }
  },

  async deleteGoodThing(goodThing: GoodThing): Promise<void> {
    try {
      // Check if created today or yesterday (using EST timezone)
      const creationDate = new Date(goodThing.date);
      const now = new Date();
      
      // Convert dates to EST timezone for comparison
      const estOptions = { timeZone: 'America/New_York' };
      
      // Format today's date as MM/DD/YYYY in EST
      const todayFormatted = now.toLocaleDateString('en-US', estOptions);
      
      // Get yesterday's date in EST timezone
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayFormatted = yesterday.toLocaleDateString('en-US', estOptions);
      
      // Format creation date as MM/DD/YYYY in EST
      const creationDateFormatted = creationDate.toLocaleDateString('en-US', estOptions);
      
      // Allow deletion if the Good Thing was created today OR yesterday
      const isWithinWindow = (todayFormatted === creationDateFormatted) || 
                            (yesterdayFormatted === creationDateFormatted);
      
      if (!isWithinWindow) {
        throw new Error('Good Things can only be deleted on the day they were created or the day after');
      }
      
      await api.delete(getFunctionUrl('goodThings'), {
        data: goodThing
      });
      
      // Clear caches to ensure fresh data
      clearAllCaches();
      
      // Trigger a full sync to update Firebase
      try {
        await this.triggerFullSync();
      } catch (syncError) {
        
        // Continue even if sync fails - we'll try again later
      }
    } catch (error) {
      
      throw error;
    }
  },

  async triggerFullSync(): Promise<{ success: boolean; result: any }> {
    try {
      const functions = getFunctions();
      const triggerSync = httpsCallable<any, any>(functions, 'triggerGoodThingsSync');
      
      const result = await triggerSync();
      
      // Clear all caches after sync
      clearAllCaches();
      
      return {
        success: true,
        result: result.data
      };
    } catch (error) {
      
      throw error;
    }
  },

  async getUserTotalGoodThingsCount(userEmail: string): Promise<number> {
    try {
      const userName = await employeeNamesService.getNameFromEmail(userEmail);
      if (!userName) {
        return 0;
      }
      
      // Check if we have cached entries
      const cacheKey = `${userEmail}_${userName}`;
      if (this.sortedEntriesCache.has(cacheKey)) {
        return this.sortedEntriesCache.get(cacheKey)!.length;
      }

      // Get entries with user filter
      const searchNames = [userName];
      if (!userName.endsWith('.')) {
        searchNames.push(`${userName}.`);
      }
      
      // Get all entries with user filter but no pagination limit
      const result = await metricsCache.getGoodThingsEntries({
        person: searchNames,
        limit: 1000 // Use a high limit to ensure we get all entries
      });
      
      // Cache the entries for future use
      const sortedEntries = result.items.sort((a, b) => {
        const aDate = new Date(a.date);
        const bDate = new Date(b.date);
        
        if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
          return bDate.getTime() - aDate.getTime();
        }
        
        return String(b.date).localeCompare(String(a.date));
      });
      
      this.sortedEntriesCache.set(cacheKey, sortedEntries);
      
      return sortedEntries.length;
    } catch (error) {
      
      return 0;
    }
  },

  async getUserRecentGoodThings(userEmail: string, limit: number = 5, offset: number = 0): Promise<GoodThing[]> {
    try {
      const userName = await employeeNamesService.getNameFromEmail(userEmail);
      if (!userName) {
        return [];
      }
      
      const cacheKey = `${userEmail}_${userName}`;

      // Check if we have cached entries
      if (this.sortedEntriesCache.has(cacheKey)) {
        const cachedEntries = this.sortedEntriesCache.get(cacheKey)!;
        return cachedEntries.slice(offset, offset + limit);
      }

      // Get entries with user filter
      const searchNames = [userName];
      if (!userName.endsWith('.')) {
        searchNames.push(`${userName}.`);
      }
      const result = await metricsCache.getGoodThingsEntries({
        person: searchNames,
        limit: 1000 // Increased from 100 to ensure all entries are fetched
      });


      // Sort entries by date
      const sortedEntries = result.items.sort((a, b) => {
        // Try to parse the dates using Date constructor
        const aDate = new Date(a.date);
        const bDate = new Date(b.date);
        
        // If both dates are valid, compare them
        if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
          return bDate.getTime() - aDate.getTime();
        }
        
        // Fallback to string comparison if Date parsing fails
        return String(b.date).localeCompare(String(a.date));
      });

      // Cache the sorted entries
      this.sortedEntriesCache.set(cacheKey, sortedEntries);

      if (offset >= sortedEntries.length) {
        return [];
      }
      
      return sortedEntries.slice(offset, offset + limit);
    } catch (error) {
      
      
      // Try to get from cache on error if we have the userName
      const userName = await employeeNamesService.getNameFromEmail(userEmail).catch(() => null);
      const cacheKey = userName ? `${userEmail}_${userName}` : null;
      
      // Only try cache if we have a valid key
      if (cacheKey && this.sortedEntriesCache.has(cacheKey)) {
        const cachedEntries = this.sortedEntriesCache.get(cacheKey)!;
        return cachedEntries.slice(offset, offset + limit);
      }
      return [];
    }
  },

  async getWeeklyStats(weekDate: Date, page: number = 1, limit: number = 5): Promise<WeeklyStats> {
    const { start, end } = getWeekBounds(weekDate);
    
    try {
      const entries = await this.getAllEntries();

      const startStr = start.toISOString().split('T')[0];
      const endStr = end.toISOString().split('T')[0];

      // Filter entries for the selected week
      const weekEntries = entries.filter(entry => {
        // Try to parse the date using Date constructor
        const entryDate = new Date(entry.date);
        
        // If date is valid, compare it with start and end dates
        if (!isNaN(entryDate.getTime())) {
          // For ISO format dates (YYYY-MM-DD), parse them as local dates to avoid timezone issues
          if (entry.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [year, month, day] = entry.date.split('-').map(Number);
            const localEntryDate = new Date(year, month - 1, day); // month is 0-indexed
            const localStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
            const localEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
            
            return localEntryDate >= localStart && localEntryDate <= localEnd;
          } else {
            // For other date formats, use the existing logic
            const localEntryDate = new Date(entryDate.getFullYear(), entryDate.getMonth(), entryDate.getDate());
            const localStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
            const localEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
            
            return localEntryDate >= localStart && localEntryDate <= localEnd;
          }
        }
        
        // Fallback to MM/DD/YY format if Date parsing fails
        try {
          const [month, day, year] = entry.date.split('/');
          if (month && day && year) {
            const formattedDate = `20${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            return formattedDate >= startStr && formattedDate <= endStr;
          }
        } catch (e) {
          
        }
        
        return false;
      });

      // Calculate top contributors
      const contributorMap = new Map<string, number>();
      weekEntries.forEach(entry => {
        const count = contributorMap.get(entry.submitter) || 0;
        contributorMap.set(entry.submitter, count + 1);
      });

      const topContributors = Array.from(contributorMap.entries())
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);

      // Calculate trending categories
      const categoryMap = new Map<string, number>();
      weekEntries.forEach(entry => {
        const count = categoryMap.get(entry.category) || 0;
        categoryMap.set(entry.category, count + 1);
      });

      const trendingCategories = Array.from(categoryMap.entries())
        .map(([category, count]) => ({ category, count }))
        .sort((a, b) => b.count - a.count);

      // Get paginated entries
      const sortedEntries = weekEntries.sort((a, b) => {
        // Try to parse the dates using Date constructor
        const aDate = new Date(a.date);
        const bDate = new Date(b.date);
        
        // If both dates are valid, compare them
        if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
          return bDate.getTime() - aDate.getTime();
        }
        
        // Fallback to string comparison if Date parsing fails
        return String(b.date).localeCompare(String(a.date));
      });
      const startIndex = (page - 1) * limit;
      const featuredEntries = sortedEntries.slice(startIndex, startIndex + limit);

      return {
        startDate: startStr,
        endDate: endStr,
        totalEntries: weekEntries.length,
        totalCount: weekEntries.length,
        uniqueContributors: contributorMap.size,
        topContributors,
        trendingCategories,
        featuredEntries,
        hasMore: startIndex + limit < weekEntries.length
      };
    } catch (error) {
      
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0],
        totalEntries: 0,
        totalCount: 0,
        uniqueContributors: 0,
        topContributors: [],
        trendingCategories: [],
        featuredEntries: [],
        hasMore: false
      };
    }
  },

  async getDashboardMetrics(userEmail?: string): Promise<DashboardMetrics> {
    try {
      // Check cache first
      const cached = loadCache(userEmail);
      const now = Date.now();
      if (cached && now - cached.timestamp < CACHE_TTL) {
        return cached.data;
      }

      const entries = await this.getAllEntries();
      const currentDate = new Date();
      const weekStart = new Date(currentDate);
      weekStart.setDate(currentDate.getDate() - ((currentDate.getDay() + 6) % 7)); // Go back to Monday
      weekStart.setHours(0, 0, 0, 0);
      const weekStartStr = weekStart.toISOString().split('T')[0];

      // Get user's recent Good Things if email is provided
      const recentUserGoodThings = userEmail ? 
        await this.getUserRecentGoodThings(userEmail) :
        [];

      const currentWeekEntries = entries.filter(entry => {
        // Try to parse the date using Date constructor
        const entryDate = new Date(entry.date);
        
        // If date is valid, compare it with weekStart
        if (!isNaN(entryDate.getTime())) {
          // For ISO format dates (YYYY-MM-DD), parse them as local dates to avoid timezone issues
          if (entry.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [year, month, day] = entry.date.split('-').map(Number);
            const localEntryDate = new Date(year, month - 1, day); // month is 0-indexed
            const localWeekStart = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate());
            
            return localEntryDate >= localWeekStart;
          } else {
            // For other date formats, use the existing logic
            const localEntryDate = new Date(entryDate.getFullYear(), entryDate.getMonth(), entryDate.getDate());
            const localWeekStart = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate());
            
            return localEntryDate >= localWeekStart;
          }
        }
        
        // Fallback to string comparison if Date parsing fails
        try {
          const [month, day, year] = entry.date.split('/');
          if (month && day && year) {
            const formattedDate = `20${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            return formattedDate >= weekStartStr;
          }
        } catch (e) {
          
        }
        
        return false;
      });

      // Calculate category distribution
      const categoryMap = new Map<string, number>();
      entries.forEach(entry => {
        const count = categoryMap.get(entry.category) || 0;
        categoryMap.set(entry.category, count + 1);
      });

      const categoryDistribution = Array.from(categoryMap.entries())
        .map(([category, count]) => ({ category, count }))
        .sort((a, b) => b.count - a.count);

      // Calculate timeline data (by month)
      const timelineMap = new Map<string, number>();
      entries.forEach(entry => {
        let monthYear: string;
        
        // Try to parse the date using Date constructor
        const entryDate = new Date(entry.date);
        
        if (!isNaN(entryDate.getTime())) {
          // Format as YYYY-MM
          const year = entryDate.getFullYear();
          const month = String(entryDate.getMonth() + 1).padStart(2, '0');
          monthYear = `${year}-${month}`;
        } else {
          // Fallback to MM/DD/YY format if Date parsing fails
          try {
            const [month, day, year] = entry.date.split('/');
            if (month && year) {
              monthYear = `20${year}-${month.padStart(2, '0')}`;
            } else {
              // If we can't parse the date, use a default bucket
              monthYear = 'unknown';
            }
          } catch (e) {
            
            monthYear = 'unknown';
          }
        }
        
        const count = timelineMap.get(monthYear) || 0;
        timelineMap.set(monthYear, count + 1);
      });

      const timeline = Array.from(timelineMap.entries())
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));

      const metrics: DashboardMetrics = {
        totalGoodThings: entries.length,
        uniqueContributors: new Set(entries.map(entry => entry.submitter)).size,
        currentWeek: currentWeekEntries.length,
        topCategories: categoryDistribution.slice(0, 3),
        categoryDistribution,
        timeline,
        recentUserGoodThings
      };

      // Save to cache
      saveCache(metrics, userEmail);
      return metrics;
    } catch (error) {
      
      return {
        totalGoodThings: 0,
        uniqueContributors: 0,
        currentWeek: 0,
        topCategories: [],
        categoryDistribution: [],
        timeline: [],
        recentUserGoodThings: []
      };
    }
  },

  async addGoodThing(goodThing: Omit<GoodThing, 'id' | 'timestamp'>): Promise<void> {
    try {
      
      // Only send to backend API, don't trigger sync
      await api.post(getFunctionUrl('goodThings'), goodThing);
      
      // Clear all caches to ensure fresh data is loaded
      clearAllCaches();
      
      // Wait a moment for Firestore to propagate the data
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (error) {
      
      throw error;
    }
  },

  async getDropdownData(): Promise<{
    categories: string[];
    people: string[];
  }> {
    try {
      // Try to get from Firebase first
      const dropdownRef = firestoreWrapper.doc(
        firestoreWrapper.collection('dropdownCache'),
        'goodThings'
      );
      const dropdownDoc = await firestoreWrapper.getDoc(dropdownRef);
      
      if (dropdownDoc.exists()) {
        const data = dropdownDoc.data();
        if (!data) {
          throw new Error('No data in dropdown cache');
        }
        return {
          categories: data.categories,
          people: data.people
        };
      }

      // If not in Firebase, fetch from API
      const response = await api.get(getFunctionUrl('goodThings/dropdown-data'));
      return response.data;
    } catch (error) {
      console.error('Error in getDropdownData:', error);
      throw error;
    }
  },

  async getSyncStatus(): Promise<{
    lastSync: Date;
    entriesCount: number;
    inProgress: boolean;
    lastError?: string;
  }> {
    try {
      const response = await api.get(getFunctionUrl('goodThings/sync/status'));
      return {
        ...response.data,
        lastSync: new Date(response.data.lastSync)
      };
    } catch (error) {
      
      throw error;
    }
  },

  async forceSync(): Promise<{ entriesCount: number; deletedCount: number }> {
    // Start a refresh session
    refreshManager.startSession();
    
    try {
      // Clear all caches before sync to ensure we start fresh
      clearAllCaches();
      
      const response = await api.post(getFunctionUrl('goodThings/sync/force'), {}, {
        timeout: 60000 // 60 second timeout for sync
      });

      const { entriesCount = 0, deletedCount = 0 } = response.data || {};

      // Queue a refresh to ensure UI updates
      refreshManager.queueRefresh(() => {
        // Clear caches again after sync to ensure we get fresh data
        clearAllCaches();
        return this.getAllEntries(); // Fetch fresh data
      });
      
      return { entriesCount, deletedCount };
    } catch (error) {
      
      
      // Even if sync fails, still clear caches and queue a refresh
      // This ensures that we at least get the latest data from Firebase
      refreshManager.queueRefresh(() => {
        clearAllCaches();
        return this.getAllEntries();
      });
      
      throw error;
    } finally {
      // Make sure to end session even if there's an error
      refreshManager.endSession();
    }
  }
};
